import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useFirestoreEvents } from '../../hooks/useFirestoreEvents';
import { useFirestoreGallery } from '../../hooks/useFirestoreGallery';
import { useAuth } from '../../hooks/useAuth';

const AdminDashboard: React.FC = () => {
  const { events, loading: eventsLoading } = useFirestoreEvents();
  const { categories, loading: galleryLoading } = useFirestoreGallery();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    toast.info('Logged out successfully. See you soon! 👋');
  };

  const totalGalleryImages = categories.reduce((sum, cat) => sum + cat.images.length, 0);

  return (
    <div className="min-h-screen bg-brand-light">
      {/* Header */}
      <div className="bg-white shadow-brand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-brand-dark">Admin Dashboard</h1>
              <p className="text-brand-blue">Neev Play School</p>
              {user && (
                <p className="text-xs text-gray-500 mt-1">
                  Logged in as: <span className="font-semibold">{user.email}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-brand-blue hover:text-brand-pink transition-colors font-medium"
              >
                View Site →
              </Link>
              <button
                onClick={handleLogout}
                className="bg-red-500 text-white px-4 py-2 rounded-bubble font-bold hover:bg-red-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-20 shadow-bubble"
          >
            <div className="text-4xl mb-2">📅</div>
            <div className="text-3xl font-bold text-brand-pink mb-1">
              {eventsLoading ? '...' : events.length}
            </div>
            <div className="text-brand-blue">Total Events</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl p-20 shadow-bubble"
          >
            <div className="text-4xl mb-2">🖼️</div>
            <div className="text-3xl font-bold text-brand-blue mb-1">
              {galleryLoading ? '...' : totalGalleryImages}
            </div>
            <div className="text-brand-blue">Gallery Images</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-20 shadow-bubble"
          >
            <div className="text-4xl mb-2">📁</div>
            <div className="text-3xl font-bold text-brand-green mb-1">
              {galleryLoading ? '...' : categories.length}
            </div>
            <div className="text-brand-blue">Gallery Categories</div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-3xl p-20 shadow-bubble"
          >
            <h2 className="text-2xl font-bold text-brand-dark mb-4">📅 Events Management</h2>
            <p className="text-brand-blue mb-6">
              Add, edit, or remove school events. Upload event photos and manage event details.
            </p>
            <Link
              to="/admin/events"
              className="inline-block bg-brand-pink text-white px-6 py-3 rounded-bubble font-bold hover:bg-opacity-90 transition-all"
            >
              Manage Events →
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-3xl p-20 shadow-bubble"
          >
            <h2 className="text-2xl font-bold text-brand-dark mb-4">🖼️ Gallery Management</h2>
            <p className="text-brand-blue mb-6">
              Manage images for each learning environment category. Add or remove photos.
            </p>
            <Link
              to="/admin/gallery"
              className="inline-block bg-brand-blue text-white px-6 py-3 rounded-bubble font-bold hover:bg-opacity-90 transition-all"
            >
              Manage Gallery →
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

